import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getRequest } from '../../../../services/httpService';
import { CHECKED_ICON, UNCHECKED_ICON } from '../../Invoice/common/InvoiceTableCommon';
import useCurrency from '../../../../components/CurrencyProvider';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PharmacyPackageBatch {
  id: number;
  batch_number: string;
  expiry_date: string;
  base_cost: number;
  base_mrp: number;
  available_stock: number;
  available_uom_id: number;
  available_uom_name: string;
  available_uom_static_field?: string;
  conversion_factor?: number;
  sgst_percent: number;
  cgst_percent: number;
  igst_percent?: number;
  is_hold?: boolean;
  store_id?: number;
}

export interface PharmacyPackageUomConversion {
  id: number | null;
  item_id: number;
  item_name: string;
  uom_id: number;
  uom_code: string;
  uom_static_field?: string;
  code: string;
  conversion_factor: number;
  converted_mrp: number;
  converted_cost_price: number;
}

export interface PharmacyPackageConsumptionItem {
  id: number;
  item_id: number;
  item_name: string;
  item_code: string;
  item_group_name: string;
  base_uom_id: number;
  base_uom_name: string;
  batches: PharmacyPackageBatch[];
  uom_conversions: PharmacyPackageUomConversion[];
  available_qty: number;
  total_quantity: number;
  quantity_consumed: number;
  quantity_remaining: number;
  discount_percent: number;
  package_pharmacy_item_id: number;
  consumption_id: number;
  rack_name: string | null;
  shelf_name: string | null;
  bin_name: string | null;
  mrp: string;
  cost_price: string;
  CGST: number;
  SGST: number;
  IGST?: number;
  pregnancy_class_id?: number;
  pregnancy_class_name?: string;
}

interface PharmacyPackageConsumptionResponse {
  package_name: string;
  package_net_amount: number;
  service_net_amount: number;
  service_amount: number;
  pharmacy_amount: number;
  used_amount: number;
  balance_amount: number;
  consumption_details: PharmacyPackageConsumptionItem[];
}

interface HeaderSummary {
  packageName: string;
  packageAmount: number;
  pharmacyAmount: number;
  usedAmount: number;
  balanceAmount: number;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PharmacyPackageItemsModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (selectedItems: PharmacyPackageConsumptionItem[]) => void;
  packageId: number | string;
  packageName: string;
  deptId?: number | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PharmacyPackageItemsModal: React.FC<PharmacyPackageItemsModalProps> = ({
  open,
  onClose,
  onSave,
  packageId,
  packageName,
  deptId,
}) => {
  const { formatCurrency } = useCurrency();
  const [rows, setRows] = useState<PharmacyPackageConsumptionItem[]>([]);
  const [summary, setSummary] = useState<HeaderSummary | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setSelectedIds([]);
      setLoadedKey(null);
    }
  }, [open]);

  // Fetch on open
  useEffect(() => {
    if (!open || !packageId) return;
    const key = `${packageId}_${deptId}`;
    if (loadedKey === key) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const deptParam = deptId ? `&dept_id=${deptId}` : '';
    getRequest(
      `invoices/packages-consumption-details/?package_id=${packageId}&pharmacy_items=true${deptParam}`
    )
      .then((res: unknown) => {
        if (cancelled) return;
        const data = res as PharmacyPackageConsumptionResponse;
        setSummary({
          packageName: data.package_name,
          packageAmount: data.package_net_amount,
          pharmacyAmount: data.pharmacy_amount,
          usedAmount: data.used_amount,
          balanceAmount: data.balance_amount,
        });
        setRows(data.consumption_details || []);
        setLoadedKey(key);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const err = e as { response?: { data?: { detail?: string } }; message?: string };
        setError(err?.response?.data?.detail || err?.message || 'Failed to load package items');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, packageId, deptId, loadedKey]);

  // Selection helpers
  const selectableRows = useMemo(
    () => rows.filter((r) => r.quantity_remaining > 0),
    [rows]
  );

  const allSelected = useMemo(
    () => selectableRows.length > 0 && selectableRows.every((r) => selectedIds.includes(r.id)),
    [selectableRows, selectedIds]
  );

  const someSelected = useMemo(
    () => selectableRows.some((r) => selectedIds.includes(r.id)) && !allSelected,
    [selectableRows, selectedIds, allSelected]
  );

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      const ids = selectableRows.map((r) => r.id);
      return prev.length === ids.length ? [] : ids;
    });
  }, [selectableRows]);

  const toggleOne = useCallback((id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const handleSave = useCallback(() => {
    const selected = rows.filter((r) => selectedIds.includes(r.id));
    onSave?.(selected);
    onClose();
  }, [rows, selectedIds, onSave, onClose]);

  const SUMMARY_ITEMS: { label: string; value: number | undefined }[] = summary
    ? [
      { label: 'Package Amount', value: summary.packageAmount },
      { label: 'Pharmacy Component', value: summary.pharmacyAmount },
      { label: 'Used Amount', value: summary.usedAmount },
      { label: 'Balance Amount', value: summary.balanceAmount },
    ]
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, m: 2, maxHeight: '96vh', display: 'flex', flexDirection: 'column' },
      }}
    >
      <DialogTitle sx={{ fontWeight: 600, pb: 0.5, pt: 2, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600, fontSize: 16, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              Select Items from Package
              {packageName && (
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: '#1976D2',
                    bgcolor: '#E3F2FD',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                  }}
                >
                  {packageName}
                </Typography>
              )}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header Summary */}
        {summary && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2.5,
              px: 3,
              py: 2,
              mb: 2,
              borderRadius: 3,
              bgcolor: '#F8FAFC',
              border: '1px solid #E5E7EB',
              flexWrap: 'nowrap',
              overflowX: 'auto',
            }}
          >
            <Box sx={{ minWidth: 140, flexShrink: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mb: 1 }}>
                Package Name
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 14 }}>
                {summary.packageName || packageName || '-'}
              </Typography>
            </Box>
            {SUMMARY_ITEMS.map(({ label, value }) => (
              <Box key={label} sx={{ minWidth: 120, flex: 1, pr: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, mb: 1 }}>
                  {label}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 14 }}>
                  {value != null ? formatCurrency(value) : '-'}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Table */}
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 'calc(96vh - 280px)',
            boxShadow: 'none',
            border: 'none',
            borderRadius: 0,
            overflowX: 'auto',
            overflowY: 'auto',
          }}
        >
          <Table size="small" sx={{ borderCollapse: 'collapse', minWidth: 900, tableLayout: 'auto' }}>
            <TableHead>
              <TableRow
                sx={{
                  background: '#F5F6F7',
                  '& .MuiTableCell-root': { borderBottom: 'none' },
                  whiteSpace: 'nowrap',
                }}
              >
                <TableCell padding="checkbox" sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>
                  <Tooltip title="Select all" arrow>
                    <Checkbox
                      size="small"
                      indeterminate={someSelected}
                      checked={allSelected}
                      onChange={toggleAll}
                      icon={UNCHECKED_ICON}
                      checkedIcon={CHECKED_ICON}
                      disableRipple
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>Item Code</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell>UOM</TableCell>
                <TableCell align="right">Available Stock</TableCell>
                <TableCell align="right">Pkg Qty</TableCell>
                <TableCell align="right">Consumed</TableCell>
                <TableCell align="right">Remaining</TableCell>
                <TableCell align="right">Discount %</TableCell>
                <TableCell align="right" sx={{ borderTopRightRadius: 12, borderBottomRightRadius: 12 }}>
                  Batches
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Loading items...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body2" color={error ? 'error' : 'text.secondary'} sx={{ py: 3 }}>
                      {error || 'No pharmacy items available in this package.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, idx) => {
                  const isSelected = selectedIds.includes(row.id);
                  const isExhausted = row.quantity_remaining <= 0;
                  const availableBatches = row.batches.filter((b) => b.available_stock > 0 && !b.is_hold);
                  return (
                    <TableRow
                      key={row.id}
                      sx={{
                        background: '#FFFFFF',
                        height: 48,
                        whiteSpace: 'nowrap',
                        opacity: isExhausted ? 0.5 : 1,
                        '& td': {
                          borderTop: idx === 0 ? 'none' : undefined,
                          borderBottom: idx === rows.length - 1 ? 'none' : undefined,
                        },
                      }}
                    >
                      <TableCell padding="checkbox" sx={{ borderTopLeftRadius: 12, borderBottomLeftRadius: 12 }}>
                        <Tooltip
                          title={isExhausted ? 'No remaining quantity' : ''}
                          arrow
                          disableHoverListener={!isExhausted}
                        >
                          <span>
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => { if (!isExhausted) toggleOne(row.id); }}
                              disabled={isExhausted}
                              icon={UNCHECKED_ICON}
                              checkedIcon={CHECKED_ICON}
                              disableRipple
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{row.item_code}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{row.item_name}</Typography>
                      </TableCell>
                      <TableCell>{row.base_uom_name}</TableCell>
                      <TableCell align="right">{row.available_qty}</TableCell>
                      <TableCell align="right">{row.total_quantity}</TableCell>
                      <TableCell align="right">{row.quantity_consumed}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ color: row.quantity_remaining === 0 ? 'error.main' : 'inherit' }}
                        >
                          {row.quantity_remaining}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{row.discount_percent}%</TableCell>
                      <TableCell
                        align="right"
                        sx={{ borderTopRightRadius: 12, borderBottomRightRadius: 12 }}
                      >
                        {availableBatches.length}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={selectedIds.length === 0}
          sx={{ borderRadius: 2 }}
        >
          Add Selected Items ({selectedIds.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PharmacyPackageItemsModal;
